import { Menu } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Header } from "@woba/ds-web-next";
import { useSidebar } from "@/core/components/ui/sidebar";
import { ROUTES } from "@/core/routes/constants";
import { isWosMenuItemActive, useWosMenu } from "../hooks/useWosMenu";

/**
 * Título da tela no Header mobile: o mesmo rótulo do item de menu da rota
 * atual ("Chat com a Bruna", "Colaboradores", "Pacotes"...). Fora do menu
 * (ex.: detalhes acessados por link), cai no nome do produto.
 */
export function useWosMobileTitle(): string {
  const [translate] = useTranslation(["core"]);
  const { items: sections } = useWosMenu();
  const { pathname } = useLocation();

  if (pathname.startsWith(ROUTES.ACTION_CENTER)) {
    return translate("app-menu-action-center") as string;
  }

  const current = sections
    .flatMap((section) => section.items)
    .find((item) => isWosMenuItemActive(item, pathname));

  return current?.title ?? (translate("app-name") as string);
}

type TrailingAction = {
  icon: (props: { color: string; size: number }) => ReactNode;
  accessibilityLabel: string;
  onPress: () => void;
};

type WosMobileHeaderProps = {
  /** Ação à direita (Figma: na Bruna, abre o histórico de IA). */
  trailingAction?: TrailingAction;
};

/**
 * Header mobile do Workbase DS (Figma 6087:23286, nota 6086:4253): Leading abre
 * a gaveta com o menu da sidebar (PED-14564), título central da tela e
 * Trailing opcional. Só aparece abaixo do breakpoint `md`, o mesmo da sidebar.
 */
export function WosMobileHeader({ trailingAction }: WosMobileHeaderProps) {
  const [translate] = useTranslation(["core"]);
  const { setOpenMobile } = useSidebar();
  const title = useWosMobileTitle();

  return (
    <div className="shrink-0 px-3 md:hidden" data-testid="wos-mobile-header">
      <Header
        heading={title}
        leading
        leadingAction={{
          accessibilityLabel: translate("app-menu-open") as string,
          icon: ({ color, size }) => (
            <Menu
              aria-hidden
              color={color}
              style={{ width: size, height: size }}
            />
          ),
          onPress: () => setOpenMobile(true),
        }}
        trailing={!!trailingAction}
        showSecondaryAction={false}
        primaryAction={
          trailingAction
            ? {
                accessibilityLabel: trailingAction.accessibilityLabel,
                icon: trailingAction.icon,
                onPress: trailingAction.onPress,
              }
            : undefined
        }
        style={{
          borderBottomLeftRadius: "var(--woba-border-radius-medium, 16px)",
          borderBottomRightRadius: "var(--woba-border-radius-medium, 16px)",
        }}
      />
    </div>
  );
}
